import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DiscordChannelMultiPicker } from '@/components/discord/DiscordPickers';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiError } from '@/lib/api';
import { useAnnouncements, useCreateAnnouncement, useDeleteAnnouncement } from '@/lib/queries';
import { useCan } from '@/lib/use-can';
import { formatDate } from '@/lib/utils';

export function AnnouncementsListPage() {
  const can = useCan();
  const list = useAnnouncements();
  const create = useCreateAnnouncement();
  const remove = useDeleteAnnouncement();

  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [channelIds, setChannelIds] = useState<string[]>([]);

  if (!can('announcements.manage')) {
    return <p className="text-muted-foreground">You don't have access to this page.</p>;
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    const ids = channelIds;
    if (ids.length === 0) {
      toast.error('Pick at least one channel');
      return;
    }
    try {
      await create.mutateAsync({
        name,
        embed: { title: name, description: 'Edit me!', color: 0x5865f2 },
        targetChannelIds: ids,
        mention: null,
      });
      toast.success('Draft created');
      setOpen(false);
      setName('');
      setChannelIds([]);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error');
    }
  }

  async function handleDelete(id: string, label: string, hasPublished: boolean) {
    const msg = hasPublished
      ? `Delete announcement "${label}" AND remove the posted messages from Discord?`
      : `Delete announcement "${label}"?`;
    if (!confirm(msg)) return;
    try {
      await remove.mutateAsync({ id, unpublish: hasPublished });
      toast.success('Deleted');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error');
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Announcements</h1>
          <p className="text-muted-foreground">
            Compose, schedule, and publish embed announcements
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              New announcement
            </Button>
          </DialogTrigger>
          <DialogContent>
            <form onSubmit={handleCreate}>
              <DialogHeader>
                <DialogTitle>New announcement</DialogTitle>
                <DialogDescription>
                  Creates a draft. Pick channels and edit the embed on the detail page.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name (internal label)</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Target channels</Label>
                  <DiscordChannelMultiPicker value={channelIds} onChange={setChannelIds} />
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={create.isPending}>
                  Create
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{list.data?.length ?? 0} announcement(s)</CardTitle>
          <CardDescription>Drafts, scheduled, and sent announcements</CardDescription>
        </CardHeader>
        <CardContent>
          {list.data && list.data.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Channels</TableHead>
                  <TableHead>Scheduled</TableHead>
                  <TableHead>Sent</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.data.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>
                      <Link to={`/announcements/${a.id}`} className="font-medium hover:underline">
                        {a.name}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Badge variant={a.status === 'sent' ? 'default' : 'secondary'}>
                        {a.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {a.targetChannelIds.length}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {a.scheduledAt ? formatDate(a.scheduledAt) : '—'}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {a.sentAt ? formatDate(a.sentAt) : '—'}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(a.id, a.name, a.publishedMessages.length > 0)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {list.data?.length === 0 && (
            <p className="text-sm text-muted-foreground">No announcements yet.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
